import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";

const API_URL = import.meta.env.VITE_API_URL || "https://eleven11-i6v4.onrender.com";

const placeholderStyles = `
 .auth-input::placeholder { color: #C7C7CC; font-weight: 400; }
 .auth-input:focus { border-color: #0f0f17 !important; background: #fff !important; }
`;

export default function AuthPageMobile() {
 const [mode, setMode] = useState("login");
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState("");
 const { refreshUser } = useAuth();

 const [form, setForm] = useState({
   firstName: "", lastName: "", email: "", phone: "", password: "",
 });

 const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

 const handleSubmit = async (e) => {
   e.preventDefault();
   setError("");
   setLoading(true);
   try {
     const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
     const body = mode === "login"
       ? { email: form.email, password: form.password }
       : { ...form, phone: form.phone.replace(/\D/g, "") };

     const res = await fetch(`${API_URL}${endpoint}`, {
       method: "POST",
       headers: { "Content-Type": "application/json" },
       body: JSON.stringify(body),
     });

     const data = await res.json();
     if (!res.ok) throw new Error(data.message || "Something went wrong");

     localStorage.setItem("token", data.token);
     localStorage.setItem("user", JSON.stringify(data.user));
     await refreshUser();
     window.location.href = data.user?.role === "admin" ? "/admin" : "/";
   } catch (err) {
     setError(err.message);
   } finally {
     setLoading(false);
   }
 };

 const inputStyle = {
   width: "100%", height: 56, borderRadius: 14,
   border: "1.5px solid #E5E5EA", background: "#FAFAFA",
   color: "#000", padding: "0 18px", fontSize: 16,
   fontWeight: 400, outline: "none", boxSizing: "border-box",
   fontFamily: "-apple-system, BlinkMacSystemFont, system-ui",
   transition: "all 0.2s ease"
 };

 return (
   <>
     <style>{placeholderStyles}</style>
     <div style={{
       minHeight: "100vh", background: "#fff",
       fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui",
       display: "flex", flexDirection: "column", padding: "0 24px",
       boxSizing: "border-box"
     }}>

       {/* Brand */}
       <div style={{ paddingTop: 48, marginBottom: 40 }}>
         <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: "#C7C7CC", marginBottom: 12 }}>
           ELEVEN MARINE
         </div>
         <h1 style={{ margin: 0, fontSize: 38, fontWeight: 800, letterSpacing: "-1.5px", color: "#0f0f17", lineHeight: 1.05 }}>
           {mode === "login" ? "Welcome\nback." : "Create your\naccount."}
         </h1>
         <p style={{ margin: "10px 0 0", fontSize: 15, color: "#8E8E93", fontWeight: 400, lineHeight: 1.5 }}>
           {mode === "login"
             ? "Sign in to access your orders, parts history and more."
             : "Join ELEVEN for faster checkout and order tracking."}
         </p>
       </div>

       {/* Tab switcher */}
       <div style={{ display: "flex", gap: 0, marginBottom: 32, borderBottom: "1px solid #F2F2F7" }}>
         {["login", "register"].map(m => (
           <button
             key={m}
             onClick={() => { setMode(m); setError(""); }}
             style={{
               flex: 1, height: 44, border: "none", background: "none",
               cursor: "pointer", fontSize: 15, fontWeight: 600,
               color: mode === m ? "#0f0f17" : "#C7C7CC",
               borderBottom: mode === m ? "2px solid #0f0f17" : "2px solid transparent",
               transition: "all 0.2s", marginBottom: -1
             }}
           >
             {m === "login" ? "Login" : "Sign Up"}
           </button>
         ))}
       </div>

       {/* Form */}
       <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>

         {mode === "register" && (
           <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
             <input className="auth-input" type="text" placeholder="First Name" value={form.firstName}
               onChange={e => updateForm("firstName", e.target.value)} style={inputStyle} required />
             <input className="auth-input" type="text" placeholder="Last Name" value={form.lastName}
               onChange={e => updateForm("lastName", e.target.value)} style={inputStyle} required />
           </div>
         )}

         {mode === "register" && (
           <input className="auth-input" type="tel" placeholder="(786) 444-9340" value={form.phone}
             onChange={e => {
               const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
               let display = digits;
               if (digits.length >= 6) display = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
               else if (digits.length >= 3) display = `(${digits.slice(0,3)}) ${digits.slice(3)}`;
               else if (digits.length > 0) display = `(${digits}`;
               updateForm("phone", display);
             }}
             style={inputStyle} required />
         )}

         <input className="auth-input" type="email" placeholder="Email address" value={form.email}
           onChange={e => updateForm("email", e.target.value)} style={inputStyle} required />

         <input className="auth-input" type="password" placeholder="Password" value={form.password}
           onChange={e => updateForm("password", e.target.value)} style={inputStyle} required minLength={8} />

         {error && (
           <div style={{ background: "#FFF2F2", border: "1px solid #FFCDD2", borderRadius: 12, padding: "12px 16px", fontSize: 14, color: "#D32F2F", fontWeight: 500 }}>
             {error}
           </div>
         )}

         <button
           type="submit"
           disabled={loading}
           style={{
             width: "100%", height: 58, borderRadius: 16, border: "none",
             background: "#0f0f17", color: "#fff", fontSize: 16, fontWeight: 700,
             cursor: loading ? "not-allowed" : "pointer", marginTop: 8,
             opacity: loading ? 0.7 : 1, letterSpacing: "-0.2px",
             fontFamily: "-apple-system, BlinkMacSystemFont, system-ui"
           }}
         >
           {loading ? "Please wait..." : mode === "login" ? "Login" : "Create Account"}
         </button>

         {/* Divider */}
         <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0" }}>
           <div style={{ flex: 1, height: 1, background: "#F2F2F7" }} />
           <span style={{ fontSize: 13, color: "#C7C7CC" }}>or</span>
           <div style={{ flex: 1, height: 1, background: "#F2F2F7" }} />
         </div>

         {/* Toggle mode */}
         <button
           type="button"
           onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
           style={{
             width: "100%", height: 58, borderRadius: 16,
             border: "1.5px solid #E5E5EA", background: "#fff",
             color: "#0f0f17", fontSize: 16, fontWeight: 600,
             cursor: "pointer", fontFamily: "-apple-system, BlinkMacSystemFont, system-ui"
           }}
         >
           {mode === "login" ? "Create an Account" : "Already have an account? Login"}
         </button>

       </form>

       {/* Footer */}
       <div style={{ padding: "24px 0 40px", textAlign: "center" }}>
         <p style={{ margin: 0, fontSize: 12, color: "#C7C7CC", lineHeight: 1.6 }}>
           By continuing, you agree to ELEVEN's{" "}
           <span style={{ color: "#8E8E93", fontWeight: 500 }}>Terms of Service</span>
           {" "}and{" "}
           <span style={{ color: "#8E8E93", fontWeight: 500 }}>Privacy Policy</span>
         </p>
       </div>

     </div>
   </>
 );
}
